import {
    AccountServiceResponse,
    UserSessionResponse,
} from '@core/generated/openapi';

import StatusEnum = AccountServiceResponse.StatusEnum;

/**
 * ⚠️ TEMPORARY PRE-LAUNCH GATE — delete this file at official launch.
 *
 * Patient AR ships to production with its feature flag OFF. The flag alone isn't
 * enough once it's switched on for early adopters: every other account would then
 * see the menu item and the marketing content, and the module currently only
 * supports RevEHR. So visibility is additionally gated on the account's Patient AR
 * Manager module.
 *
 * Three DIFFERENT rules, one per surface — they are not interchangeable:
 *
 * | Module status        | Patient AR nav | Patient AR routes | Admin > Settings |
 * |----------------------|----------------|-------------------|------------------|
 * | (no module)          | hidden         | home              | hidden           |
 * | NEW                  | shown          | marketing         | hidden           |
 * | PENDING_ONBOARDING   | shown          | marketing         | hidden           |
 * | ONBOARDING           | shown          | marketing         | shown            |
 * | WAITING_FOR_STARTUP, | shown          | marketing         | shown            |
 * | IN_STARTUP,          |                |                   |                  |
 * | WAITING_TO_GO_LIVE,  |                |                   |                  |
 * | ON_HOLD, LIVE        |                |                   |                  |
 * | ACTIVE               | shown          | module            | shown            |
 * | PENDING_TERMINATION  | shown          | module            | shown            |
 * | TERMINATED           | hidden         | home              | shown (*)        |
 *
 * (*) Terminated keeps Admin > Settings reachable because that screen already
 * renders its own "Patient AR is not active on this account" placeholder — see
 * PatientArSettingsComponent.isSubscribed. The module's own data is retained and
 * dormant, not deleted.
 *
 * "marketing" is `patient-ar/notSubscribed` (Learn More / Schedule a Tour), which is
 * the behaviour these statuses had before the gate existed and is deliberately
 * preserved.
 *
 * Each predicate is ANDed with the existing feature-flag check at its call site, so
 * removing the gate at launch means deleting this file and the predicate terms that
 * reference it — leaving the flag checks exactly as they were.
 */

/**
 * The account carries the Patient AR Manager module in any state but Terminated.
 *
 * Drives the Patient AR NAV ITEM and eligibility for the marketing page. Note this
 * is NOT enough to enter the module itself — see `isPatientArLive`.
 *
 * Read off the user session, which loads at login / page load, so adding the module
 * or changing its status takes effect on the next load with no deploy.
 */
export function hasPatientArModule(
    user: UserSessionResponse | null | undefined,
): boolean {
    const status = patientArModule(user)?.status;
    return !!status && status !== StatusEnum.Terminated;
}

/**
 * The module is far enough along to actually use.
 *
 * ⚠️ An ALLOWLIST, and deliberately the same one `AgingSubscribedGuard` uses. Every
 * other status falls through to the marketing page rather than the module, which is
 * the pre-existing behaviour — don't widen it to "not Terminated".
 */
export function isPatientArLive(
    user: UserSessionResponse | null | undefined,
): boolean {
    const status = patientArModule(user)?.status;
    return (
        status === StatusEnum.Active || status === StatusEnum.PendingTermination
    );
}

/**
 * Admin > Settings visibility.
 *
 * Its own rule, narrower than the nav's at the front and wider at the back: an
 * account that has only signed up (New / Pending Onboarding) has nothing to
 * configure yet, while a Terminated one still gets in because the screen shows its
 * own not-active placeholder.
 */
export function canAccessPatientArSettings(
    user: UserSessionResponse | null | undefined,
): boolean {
    const status = patientArModule(user)?.status;
    return (
        !!status &&
        status !== StatusEnum.New &&
        status !== StatusEnum.PendingOnboarding
    );
}

/** The account's Patient AR service record, whatever its status. */
function patientArModule(
    user: UserSessionResponse | null | undefined,
): AccountServiceResponse | undefined {
    return user?.accountModules?.find(
        (service) =>
            service.serviceType ===
            AccountServiceResponse.ServiceTypeEnum.PatientAr,
    );
}
